import { useCallback, useState } from "react";
import AuthPromptDialog from "@/components/Auth/AuthPromptDialog";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/analytics";

interface Options {
  /** Copy shown in the dialog body. Specific > generic. */
  reason: string;
  /** Feature id for GA attribution (`home_spot`, `multicam`, `feed`, etc.). */
  source: string;
}

/**
 * Wrap any callback so it's a no-op for anon users (opens the
 * `AuthPromptDialog` instead) and runs normally once signed in.
 *
 * Usage:
 *
 *   const { gate, promptNode } = useRequireAuth({
 *     reason: "Sign in to save your home spot.",
 *     source: "home_spot",
 *   });
 *   ...
 *   <Button onClick={gate(handleSetHome)}>⭐</Button>
 *   {promptNode}
 *
 * The `promptNode` must be rendered somewhere in the component's JSX
 * so the dialog has a mount point. Keeping it as a returned element
 * (rather than a global portal) means each gate owns its own dialog
 * state — simpler reasoning, and multiple gates on one page don't
 * fight over the single-modal pattern.
 */
export function useRequireAuth({ reason, source }: Options) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const gate = useCallback(
    <A extends unknown[], R>(fn: (...args: A) => R) =>
      (...args: A): R | undefined => {
        if (user) return fn(...args);
        trackEvent("auth_gate_shown", { source });
        setOpen(true);
        return undefined;
      },
    [user, source],
  );

  const promptNode = (
    <AuthPromptDialog
      open={open}
      onClose={() => setOpen(false)}
      reason={reason}
      source={source}
    />
  );

  return { gate, promptNode, isAuthed: !!user };
}
